import { useState } from "react";
import axiosService from "../../core/services/axios-service";
import { ApiRoutes } from "../../core/dictionary/api-url";
import ToastService from "../../core/services/toaster-service";
import "../../assets/scss/pre-team-course-survey.scss";
import "../../assets/scss/in-person-training-feedback.scss";

const LIKERT_OPTIONS = [
  "Strongly Agree",
  "Agree",
  "Neutral",
  "Disagree",
  "Strongly Disagree",
];

const LIKERT_QUESTIONS = [
  {
    key: "purposeMet",
    label: "The overall purpose of this program was met",
  },
  {
    key: "lengthAdequate",
    label: "Length of presentation and activities were adequate",
  },
  {
    key: "objectivesMet",
    label: "The program met the stated objectives",
  },
  {
    key: "applyToPractice",
    label: "I will be able to apply this information to my practice",
  },
  {
    key: "contentCurrent",
    label: "Activity objectives and content reflected current practice issues",
  },
  {
    key: "teachingEffective",
    label: "The teaching methods were effective",
  },
];

const initialForm = {
  email: "",
  purposeMet: "",
  lengthAdequate: "",
  objectivesMet: "",
  applyToPractice: "",
  contentCurrent: "",
  teachingEffective: "",
};

export default function RxnReadyCourseEvaluation() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.email.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email))
      return "Please enter a valid email address.";
    for (const q of LIKERT_QUESTIONS) {
      if (!form[q.key]) return `Please answer: "${q.label}"`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        Email: form.email,
        "The overall purpose of this program was met": form.purposeMet,
        "Length of presentation and activities were adequate":
          form.lengthAdequate,
        "The program met the stated objectives": form.objectivesMet,
        "I will be able to apply this information to my practice":
          form.applyToPractice,
        "Activity objectives and content reflected current practice issues":
          form.contentCurrent,
        "The teaching methods were effective": form.teachingEffective,
      };

      const res = await axiosService.post(
        ApiRoutes.RXN_READY_COURSE_EVALUATION,
        payload
      );
      if (res.data && res.data.success) {
        ToastService.success(
          res.data.message || "Evaluation submitted successfully!"
        );
        setSubmitted(true);
      } else {
        const msg =
          res.data?.message ||
          "Failed to submit evaluation. Please try again.";
        setError(msg);
        ToastService.error(msg);
      }
    } catch (err) {
      const msg = err?.message || "Server error. Please try again.";
      setError(msg);
      ToastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="survey-page">
      {/* Header */}
      <header className="survey-header">
        <img
          src="https://res.cloudinary.com/dhhzpdckr/image/upload/v1758128880/rescuernlogo_dsi3mf.png"
          alt="RescueRn Logo"
          className="survey-header__logo"
        />
      </header>

      {/* Main content */}
      <main className="survey-main">
        {submitted ? (
          <div className="survey-thankyou">
            <div className="survey-thankyou__icon">✓</div>
            <h2 className="survey-thankyou__title">Thank You!</h2>
            <p className="survey-thankyou__message">
              Your course evaluation has been submitted successfully. Your
              feedback helps us continually improve the RXN Ready program.
            </p>
          </div>
        ) : (
          <div className="survey-card">
            <div className="survey-card__intro">
              <h1 className="survey-card__title">RXN Ready Course Evaluation</h1>
              <p className="survey-card__subtitle">
                Please take a moment to evaluate your experience with the RXN
                Ready course.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Your Information</h3>
                <div className="survey-field survey-field--full">
                  <label className="survey-label" htmlFor="email">
                    Email <span className="survey-required">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    className="survey-input"
                    placeholder="Enter your email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Likert questions */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Course Evaluation</h3>
                <p className="survey-section__description">
                  Please rate your agreement with each statement below.
                </p>

                <div className="feedback-likert-table">
                  {/* Header row */}
                  <div className="feedback-likert-header">
                    <div className="feedback-likert-header__statement" />
                    {LIKERT_OPTIONS.map((opt) => (
                      <div
                        key={opt}
                        className="feedback-likert-header__option"
                      >
                        {opt}
                      </div>
                    ))}
                  </div>

                  {/* Question rows */}
                  {LIKERT_QUESTIONS.map((q, idx) => (
                    <div
                      key={q.key}
                      className={`feedback-likert-row${
                        idx % 2 === 0 ? " feedback-likert-row--even" : ""
                      }`}
                    >
                      <div className="feedback-likert-row__statement">
                        {q.label}{" "}
                        <span className="survey-required">*</span>
                      </div>
                      {LIKERT_OPTIONS.map((opt) => (
                        <label
                          key={opt}
                          className="feedback-likert-row__cell"
                          title={opt}
                        >
                          <input
                            type="radio"
                            name={q.key}
                            value={opt}
                            checked={form[q.key] === opt}
                            onChange={handleChange}
                            className="survey-radio"
                          />
                          <span className="feedback-likert-row__mobile-label">
                            {opt}
                          </span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {error && <div className="survey-error">{error}</div>}

              <button
                type="submit"
                className="survey-submit-btn"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Evaluation"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
